import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { site, marqueeWords } from "../../data/site";
import { useContact } from "../ContactProvider";
import CursorSpotlight from "../CursorSpotlight";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = (delay = 0, gap = 0.08) => ({
  hidden: {},
  show: { transition: { delayChildren: delay, staggerChildren: gap } },
});

const wordReveal = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const { open } = useContact();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-between pt-28 pb-10 overflow-hidden"
    >
      {/* cursor-follow chartreuse spotlight */}
      <CursorSpotlight color="rgba(227,255,52,0.07)" />

      {/* dark purple ambient glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 -left-40 w-[700px] h-[700px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, #2A0A3D 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-20 -right-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, #4A1D6E 0%, transparent 70%)" }}
      />

      {/* scan line */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E3FF34]/40 to-transparent scanline"
      />

      {/* meta strip */}
      <motion.div
        initial="hidden"
        animate="show"
        variants={stagger(0.1, 0.06)}
        className="max-w-[1600px] w-full mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]"
      >
        <motion.div variants={fadeUp} data-testid="hero-meta-est">
          <span className="text-[#E3FF34]">[ 01 ]</span> Mark Cassara — Founder
        </motion.div>
        <motion.div variants={fadeUp} className="hidden md:block">
          <span className="text-[#E3FF34]">[ 02 ]</span> Full-stack studio
        </motion.div>
        <motion.div variants={fadeUp} className="hidden md:block">
          <span className="text-[#E3FF34]">[ 03 ]</span> Celina, TX
        </motion.div>
        <motion.div variants={fadeUp} className="text-right flex items-center justify-end gap-2">
          <span className="w-1.5 h-1.5 bg-[#E3FF34] inline-block pulse-halo" />
          <span><span className="text-[#E3FF34]">[ 04 ]</span> National ops</span>
        </motion.div>
      </motion.div>

      {/* main type */}
      <div className="relative max-w-[1600px] w-full mx-auto px-6 lg:px-10 flex-1 flex flex-col justify-center py-16">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger(0.05, 0.1)}
          className="font-['Anton'] uppercase leading-[0.84] tracking-tight text-[#F2F2F2]"
        >
          <h1
            data-testid="hero-heading"
            aria-label="Marketing that compounds"
            className="text-[16vw] md:text-[15vw] lg:text-[12vw] xl:text-[11vw]"
          >
            <span className="block overflow-hidden">
              <motion.span variants={wordReveal} className="inline-block">
                Marketing
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={wordReveal} className="inline-block">
                that{" "}
                <span className="font-['Outfit'] font-light tracking-tight text-[#E3FF34]">
                  <em>compounds.</em>
                </span>
              </motion.span>
            </span>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <p
            data-testid="hero-intro"
            className="md:col-span-6 lg:col-span-5 font-['Outfit'] font-light text-[#A3A3A3] text-base md:text-lg leading-relaxed"
          >
            {site.intro}
          </p>
          <div className="md:col-span-6 lg:col-span-7 flex flex-wrap items-center gap-4 md:justify-end">
            <a
              href="#work"
              data-testid="hero-cta-work"
              className="btn-shimmer relative inline-flex items-center gap-3 bg-[#E3FF34] text-[#050505] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-xs px-7 py-4 border border-[#E3FF34] hover:bg-transparent hover:text-[#E3FF34] transition-all duration-300"
            >
              <span className="relative z-10 inline-flex items-center gap-3">
                See the work
                <ArrowUpRight size={14} />
              </span>
            </a>
            <button
              type="button"
              onClick={() => open()}
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-3 font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-xs px-7 py-4 border border-[#2E2E38] text-[#F2F2F2] hover:border-[#E3FF34] hover:text-[#E3FF34] transition-colors"
            >
              Start a project
              <ArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
          </div>
        </motion.div>
      </div>

      {/* scroll cue + marquee */}
      <div className="max-w-[1600px] w-full mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] pb-6">
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="flex items-center gap-2"
          >
            <ArrowDown size={12} className="text-[#E3FF34]" />
            Scroll
          </motion.span>
          <span>Selected clients — 2024 / 2025</span>
        </div>
      </div>

      <div className="border-y border-[#2E2E38] py-6 bg-[#0A0A12]">
        <Marquee
          gradient={false}
          speed={36}
          pauseOnHover
          className="font-['JetBrains_Mono'] uppercase tracking-[0.3em] text-sm md:text-base text-[#D4D4D8]"
        >
          {marqueeWords.concat(marqueeWords).map((w, i) => (
            <span key={i} className="px-8 flex items-center gap-8">
              <span className="text-[#E3FF34]">/</span>
              {w}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
